import { EmptyState } from '@/components/ui/EmptyState';
import { Link, useParams } from 'react-router';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import { LeaderboardTable } from '../components/LeaderboardTable';
import { RaceShowcase } from '../components/RaceShowcase';
import { formatMoney } from '../format';
import { useLeaderboard, useRace } from '../api';

/**
 * How many places the leaderboard shows.
 *
 * Ten because ten is what the prize table pays: every rank below is somebody who
 * finished, won nothing, and would rather not be listed. The caller's own row is
 * still pinned underneath wherever it sits — "where am I" is the question they
 * came with, and it is their own data.
 */
const TopCount = 10;

/**
 * One race in full: the prize table, the podium and the top of the leaderboard.
 */
export function RaceDetailPage() {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const auth = useAuthDialog();

  const race = useRace(slug);
  const leaderboard = useLeaderboard(slug, 1, TopCount);

  if (race.isLoading) {
    return (
      <p className="py-20 text-center text-surface-500" role="status">
        Yükleniyor…
      </p>
    );
  }

  if (race.isError || !race.data) {
    return (
      <EmptyState title="Böyle bir yarışma bulunamadı.">
        <Link
          to="/wager-races"
          className="mt-4 inline-block text-sm text-brand-400 hover:underline"
        >
          Tüm yarışmalar
        </Link>
      </EmptyState>
    );
  }

  const data = race.data;

  return (
    <div className="w-full py-2">
      <Link
        to="/wager-races"
        className="text-sm text-surface-500 transition hover:text-surface-300"
      >
        ← Tüm yarışmalar
      </Link>

      <div className="mt-5">
        <RaceShowcase race={data} detail />{' '}
      </div>
      {data.description ? (
        <p className="race-rules mt-5 whitespace-pre-line text-sm leading-relaxed text-surface-400">
          {data.description}
        </p>
      ) : null}

      {!isAuthenticated ? (
        <p className="mt-8 rounded-xl border border-brand-500/25 bg-brand-500/[0.06] px-4 py-3 text-sm text-surface-300">
          Sıralamada kendinizi görmek için{' '}
          <button
            type="button"
            onClick={() => auth.open('login')}
            className="font-medium text-brand-400 hover:underline"
          >
            giriş yapın
          </button>{' '}
          ve profilinizden casino kullanıcı adınızı kaydedin.
        </p>
      ) : null}

      <div className="race-detail-grid">
        <section aria-labelledby="leaderboard" className="mt-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 id="leaderboard" className="section-title">
              Sıralama · İlk {TopCount}
            </h2>

            {data.standingsUpdatedAtUtc ? (
              <p className="text-xs text-surface-600">
                Son güncelleme:{' '}
                {new Intl.DateTimeFormat('tr-TR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }).format(new Date(data.standingsUpdatedAtUtc))}
              </p>
            ) : null}
          </div>

          <div className="race-leaderboard overflow-hidden rounded-[var(--radius-card)] border border-surface-800 bg-surface-900/60 p-2 sm:p-4">
            {leaderboard.isLoading ? (
              <p className="py-12 text-center text-sm text-surface-500" role="status">
                Yükleniyor…
              </p>
            ) : leaderboard.data && leaderboard.data.rows.length > 0 ? (
              <LeaderboardTable
                rows={leaderboard.data.rows}
                currency={data.currency}
                you={leaderboard.data.you}
              />
            ) : (
              <p className="py-12 text-center text-sm text-surface-500">
                {data.phase === 'upcoming'
                  ? 'Yarışma henüz başlamadı.'
                  : 'Sıralama henüz yüklenmedi.'}
              </p>
            )}
          </div>
        </section>

        {data.prizes.length > 0 ? (
          <section aria-labelledby="prizes" className="mt-8">
            <h2 id="prizes" className="section-title">
              Ödül dağılımı
            </h2>

            <ol className="race-prizes mt-3 grid gap-2">
              {data.prizes.map((prize) => (
                <li
                  key={prize.rank}
                  className="race-prize-row flex items-center justify-between px-4 py-3 text-sm"
                >
                  <span className="text-surface-400">{prize.rank}. sıra</span>
                  <span className="font-medium text-surface-100">
                    {prize.label ?? formatMoney(prize.amount, data.currency)}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </div>
    </div>
  );
}
